import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import DoctoresService from "../../services/DoctoresService";
import CajaPagosService from "../../services/CajaPagosService";
import CajaService from "../../services/CajaService";
import EgresosIngresosService from "../../services/EgresosIngresosService";
import ReembolsoService from "../../services/ReembolsoService";
import HistorialService from "../../services/HistorialService";
import TelefonosService from "../../services/TelefonosService";
import HorariosAtencionService from "../../services/HorariosAtencionService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";
import RefreshIcon from "@mui/icons-material/Refresh";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Paper,
  Typography,
  OutlinedInput,
  Stack,
  Box,
  IconButton,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Chip,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const ReporteCaja = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = usuarioString ? JSON.parse(usuarioString) : {};

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [doctores, setDoctores] = useState([]);
  const [reembolsosList, setReembolsosList] = useState([]);
  const [egresosIngresosList, setEgresosIngresosList] = useState([]);
  const [historialEventosMap, setHistorialEventosMap] = useState({});
  const [cajaMap, setCajaMap] = useState({});
  const [pagosPorSolicitudMap, setPagosPorSolicitudMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("Todas");
  const [doctorSeleccionado, setDoctorSeleccionado] = useState("Todos");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  // Paginación por día (solo para días con registros)
  const [paginaDia, setPaginaDia] = useState(1);

  useEffect(() => {
    document.title = "Reporte de Caja";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }

    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }
    return null;
  };

  const formatDateToYYYYMMDD = (dt) => {
    if (!dt) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resDoc, resEI, resCaja] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        DoctoresService.getDoctores().catch(() => ({ data: [] })),
        EgresosIngresosService.getEgresosIngresos().catch(() => ({ data: [] })),
        CajaService.getCaja().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
          ? resSol.data
          : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
          ? resSuc.data
          : [];

      const listaDoc = Array.isArray(resDoc?.data?.doctores)
        ? resDoc.data.doctores
        : Array.isArray(resDoc?.data)
          ? resDoc.data
          : [];

      const listaEI = Array.isArray(resEI?.data?.egresosIngresos)
        ? resEI.data.egresosIngresos
        : Array.isArray(resEI?.data)
          ? resEI.data
          : [];

      const listaCaja = Array.isArray(resCaja?.data?.caja)
        ? resCaja.data.caja
        : Array.isArray(resCaja?.caja)
          ? resCaja.caja
          : Array.isArray(resCaja?.data)
            ? resCaja.data
            : [];

      const cMap = {};
      listaCaja.forEach((c) => {
        if (c.idSolicitud) {
          cMap[c.idSolicitud] = c;
        }
      });
      setCajaMap(cMap);

      // Cargar pagos desglosados individuales por caja/solicitud (al estilo Corte de Caja)
      const pMap = {};
      await Promise.all(
        listaSol.map(async (sol) => {
          const idCaja = sol.idCaja || cMap[sol.idSolicitud]?.idCaja;
          if (!idCaja) return;
          try {
            const resP = await CajaPagosService.getPagosPorCaja(idCaja);
            const arrP = Array.isArray(resP?.pagos)
              ? resP.pagos
              : Array.isArray(resP?.data?.pagos)
                ? resP.data.pagos
                : Array.isArray(resP)
                  ? resP
                  : [];
            pMap[sol.idSolicitud] = arrP;
          } catch (e) {
            pMap[sol.idSolicitud] = [];
          }
        })
      );
      setPagosPorSolicitudMap(pMap);

      // OBTENCIÓN RÁPIDA DE REEMBOLSOS
      const rawReembolsos = [];

      // 1. Reembolsos desde endpoint general (si existe)
      try {
        const resGeneral = await ReembolsoService.getReembolsos();
        const payload = resGeneral?.data?.reembolsos ?? resGeneral?.data;
        if (Array.isArray(payload)) rawReembolsos.push(...payload);
      } catch (e) { }

      // 2. Reembolsos de cortes actuales por sucursal (ReembolsoService.getReembolsoActual)
      if (listaSuc.length > 0) {
        try {
          const reemProms = listaSuc.map((suc) =>
            ReembolsoService.getReembolsoActual(suc.idSucursal).catch(() => null)
          );
          const reemResps = await Promise.all(reemProms);

          reemResps.forEach((res, idx) => {
            const sucId = listaSuc[idx]?.idSucursal;
            const payload = res?.data?.reembolsos ?? res?.data;
            const arr = Array.isArray(payload) ? payload : payload ? [payload] : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push({
                  ...r,
                  idSucursal: r.idSucursal ?? r.sucursalId ?? sucId,
                });
              }
            });
          });
        } catch (e) { }
      }

      // 3. Reembolsos de cortes anteriores (CajaService.getCortes)
      try {
        const resCortes = await CajaService.getCortes().catch(() => null);
        const cortesList = Array.isArray(resCortes?.data) ? resCortes.data : Array.isArray(resCortes) ? resCortes : [];
        if (cortesList.length > 0) {
          const cortesProms = cortesList.map((c) =>
            ReembolsoService.getReembolsoPorCorte(c.idSucursal || c.sucursalId, c.idCorte || c.idCorteReembolso).catch(() => null)
          );
          const cortesResps = await Promise.all(cortesProms);
          cortesResps.forEach((res) => {
            const payload = res?.reembolsos ?? res?.data?.reembolsos ?? res;
            const arr = Array.isArray(payload) ? payload : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push(r);
              }
            });
          });
        }
      } catch (e) { }

      // Deduplicación limpia
      const seenKeys = new Set();
      const listaFinalReembolsos = [];

      rawReembolsos.forEach((r) => {
        const key = r.idReembolso
          ? `id:${r.idReembolso}`
          : `${r.idSucursal || r.sucursalId}-${r.fecha}-${r.monto || r.cantidad}-${r.motivo || ""}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          listaFinalReembolsos.push(r);
        }
      });

      setSolicitudes(listaSol);
      setSucursales(listaSuc);
      setDoctores(listaDoc);
      setEgresosIngresosList(listaEI);
      setReembolsosList(listaFinalReembolsos);

      // Sucursal por defecto = sucursal actual del usuario
      if (currentU.sucursal) {
        const foundSuc = listaSuc.find(
          (s) => String(s.idSucursal) === String(currentU.sucursal) || s.nombre === currentU.sucursal
        );
        if (foundSuc) {
          setSucursalSeleccionada(String(foundSuc.idSucursal));
        } else {
          setSucursalSeleccionada(String(currentU.sucursal));
        }
      }

      // Cargar historial de eventos para todas las solicitudes
      const evtMap = {};
      await Promise.all(
        listaSol.map(async (sol) => {
          if (sol.idSolicitud) {
            try {
              const evts = await HistorialService.getHistorialEventos(sol.idSolicitud);
              if (evts && evts.length > 0) evtMap[sol.idSolicitud] = evts;
            } catch (e) { }
          }
        })
      );
      setHistorialEventosMap(evtMap);
    } catch (error) {
      console.error("Error cargando datos para Reporte de Caja:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al obtener la información de caja del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper robusto para normalizar y comparar doctor en solicitud
  const coincideDoctor = (sol, doctorId) => {
    if (doctorId === "Todos" || !doctorId) return true;
    if (!sol) return false;

    const targetIdStr = String(doctorId);
    const targetDoc = doctores.find((d) => String(d.idDoctor) === targetIdStr);

    const targetNom = targetDoc
      ? `${targetDoc.nombre || ""} ${targetDoc.apPaterno || ""} ${targetDoc.apMaterno || ""}`.trim().toLowerCase()
      : "";

    // 1. Arreglo idDoctores o idDoctoresN
    const arrIds = Array.isArray(sol.idDoctores)
      ? sol.idDoctores
      : Array.isArray(sol.idDoctoresN)
        ? sol.idDoctoresN
        : [];

    if (arrIds.some((id) => id !== undefined && id !== null && String(id) === targetIdStr)) {
      return true;
    }

    // 2. Campos individuales de ID de doctor
    const directIds = [
      sol.idDoctor,
      sol.idDoctor2,
      sol.idDoctor3,
      sol.id_doctor,
      sol.id_doctor2,
      sol.id_doctor3,
      sol.doctorId,
      sol.idDoctorRemitido,
      sol.doctor?.idDoctor,
      sol.doctor?.id,
    ];

    if (directIds.some((id) => id !== undefined && id !== null && String(id) === targetIdStr)) {
      return true;
    }

    // 3. Campos de Nombre de doctor
    const names = [
      sol.nombreDoctor,
      sol.nombreDoctor2,
      sol.nombreDoctor3,
      sol.doctorRemitido,
      typeof sol.doctor === "string" ? sol.doctor : null,
      sol.doctorNombre,
      `${sol.nombreDoctor || ""} ${sol.apPaternoDoctor || ""} ${sol.apMaternoDoctor || ""}`,
    ];

    if (targetNom) {
      for (const nameVal of names) {
        if (typeof nameVal === "string" && nameVal.trim() !== "") {
          const cleaned = nameVal.trim().toLowerCase();
          if (cleaned.includes(targetNom) || targetNom.includes(cleaned)) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // Helper para normalizar sucursal en solicitud
  const coincideSucursal = (item, sucId) => {
    if (sucId === "Todas") return true;
    const idSucSol = String(item.idSucursal || item.sucursalId || item.sucursal || "");
    const nomSucSol = String(item.nombreSucursal || item.sucursal || "").trim().toLowerCase();

    const targetSuc = sucursales.find(
      (s) => String(s.idSucursal) === String(sucId) || s.nombre === sucId
    );

    if (targetSuc) {
      const targetNom = String(targetSuc.nombre).trim().toLowerCase();
      return idSucSol === String(targetSuc.idSucursal) || nomSucSol === targetNom;
    }
    return idSucSol === String(sucId);
  };

  // Generar lista ÚNICAMENTE de días que contienen registros (solicitudes, reembolsos, egresos o ingresos)
  const diasConRegistros = useMemo(() => {
    const datesSet = new Set();
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
    if (!start || !end) return [];

    const minTime = start.getTime();
    const maxTime = end.getTime();

    // 1. Días de solicitudes
    solicitudes.forEach((sol) => {
      const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
      if (estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "")) return;
      if (!coincideSucursal(sol, sucursalSeleccionada)) return;
      if (!coincideDoctor(sol, doctorSeleccionado)) return;

      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (dt && dt.getTime() >= minTime && dt.getTime() <= maxTime) {
        datesSet.add(formatDateToYYYYMMDD(dt));
      }
    });

    // 2. Días de reembolsos
    reembolsosList.forEach((r) => {
      if (!coincideSucursal(r, sucursalSeleccionada)) return;
      const dt = parseFechaDate(r.fecha || r.created_at || r.fechaReembolso || r.createdAt);
      if (dt && dt.getTime() >= minTime && dt.getTime() <= maxTime) {
        datesSet.add(formatDateToYYYYMMDD(dt));
      }
    });

    // 3. Días de egresos/ingresos
    egresosIngresosList.forEach((item) => {
      if (!coincideSucursal(item, sucursalSeleccionada)) return;
      const dt = parseFechaDate(item.fecha || item.created_at);
      if (dt && dt.getTime() >= minTime && dt.getTime() <= maxTime) {
        datesSet.add(formatDateToYYYYMMDD(dt));
      }
    });

    return Array.from(datesSet).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, reembolsosList, egresosIngresosList, sucursalSeleccionada, doctorSeleccionado, fechaDe, fechaA, sucursales, doctores]);

  // Al cambiar cualquier filtro, reiniciar paginación al primer día con registros
  useEffect(() => {
    setPaginaDia(1);
  }, [fechaDe, fechaA, sucursalSeleccionada, doctorSeleccionado]);

  const diaActivo = diasConRegistros[paginaDia - 1] || (diasConRegistros.length > 0 ? diasConRegistros[0] : fechaDe);

  // Ventana deslizante para mostrar Máximo 10 días iniciales / activos
  const paginasVisibles = useMemo(() => {
    const total = diasConRegistros.length;
    if (total <= 10) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(1, paginaDia - 4);
    let end = start + 9;

    if (end > total) {
      end = total;
      start = Math.max(1, end - 9);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }, [diasConRegistros.length, paginaDia]);

  // Solicitudes filtradas para el DÍA ACTIVO
  const solicitudesDia = useMemo(() => {
    if (!diaActivo) return [];
    return solicitudes.filter((sol) => {
      const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
      if (estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "")) {
        return false;
      }

      if (!coincideSucursal(sol, sucursalSeleccionada)) return false;
      if (!coincideDoctor(sol, doctorSeleccionado)) return false;

      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (!dt) return false;
      return formatDateToYYYYMMDD(dt) === diaActivo;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, sucursalSeleccionada, doctorSeleccionado, diaActivo, sucursales, doctores]);

  // Solicitudes filtradas para el RANGO COMPLETO (para PDF)
  const solicitudesRangoCompleto = useMemo(() => {
    return solicitudes.filter((sol) => {
      const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
      if (estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "")) {
        return false;
      }

      if (!coincideSucursal(sol, sucursalSeleccionada)) return false;
      if (!coincideDoctor(sol, doctorSeleccionado)) return false;

      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (!dt) return false;

      const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
      const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
      if (!start || !end) return false;

      return dt.getTime() >= start.getTime() && dt.getTime() <= end.getTime();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, sucursalSeleccionada, doctorSeleccionado, fechaDe, fechaA, sucursales, doctores]);

  // Reembolsos filtrados para el DÍA ACTIVO
  const reembolsosDia = useMemo(() => {
    if (!diaActivo) return [];
    return reembolsosList.filter((r) => {
      if (!coincideSucursal(r, sucursalSeleccionada)) return false;
      const dt = parseFechaDate(r.fecha || r.created_at || r.fechaReembolso || r.createdAt);
      return dt && formatDateToYYYYMMDD(dt) === diaActivo;
    });
  }, [reembolsosList, sucursalSeleccionada, diaActivo, sucursales]);

  // Reembolsos filtrados para el RANGO COMPLETO (para PDF)
  const reembolsosRangoCompleto = useMemo(() => {
    return reembolsosList.filter((r) => {
      if (!coincideSucursal(r, sucursalSeleccionada)) return false;
      const dt = parseFechaDate(r.fecha || r.created_at || r.fechaReembolso || r.createdAt);
      if (!dt) return false;
      const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
      const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
      if (!start || !end) return false;
      return dt.getTime() >= start.getTime() && dt.getTime() <= end.getTime();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reembolsosList, sucursalSeleccionada, fechaDe, fechaA, sucursales]);

  // Egresos e Ingresos filtrados para el RANGO COMPLETO (para PDF)
  const egresosRangoCompleto = useMemo(() => {
    return egresosIngresosList.filter((e) => {
      const tipo = `${e.operacion ?? e.tipo ?? e.tipoMovimiento ?? ""}`.toLowerCase();
      const esEgreso = tipo.includes("egreso") || tipo.includes("gasto");
      if (!esEgreso) return false;
      if (!coincideSucursal(e, sucursalSeleccionada)) return false;
      const dt = parseFechaDate(e.fecha || e.created_at);
      if (!dt) return false;
      const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
      const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
      if (!start || !end) return false;
      return dt.getTime() >= start.getTime() && dt.getTime() <= end.getTime();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [egresosIngresosList, sucursalSeleccionada, fechaDe, fechaA, sucursales]);

  const ingresosRangoCompleto = useMemo(() => {
    return egresosIngresosList.filter((i) => {
      const tipo = `${i.operacion ?? i.tipo ?? i.tipoMovimiento ?? ""}`.toLowerCase();
      const esIngreso = tipo.includes("ingreso") || tipo.includes("entrada");
      if (!esIngreso) return false;
      if (!coincideSucursal(i, sucursalSeleccionada)) return false;
      const dt = parseFechaDate(i.fecha || i.created_at);
      if (!dt) return false;
      const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
      const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
      if (!start || !end) return false;
      return dt.getTime() >= start.getTime() && dt.getTime() <= end.getTime();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [egresosIngresosList, sucursalSeleccionada, fechaDe, fechaA, sucursales]);

  // Egresos e Ingresos filtrados para el DÍA ACTIVO
  const egresosDia = useMemo(() => {
    if (!diaActivo) return [];
    return egresosIngresosList.filter((e) => {
      const tipo = `${e.operacion ?? e.tipo ?? e.tipoMovimiento ?? ""}`.toLowerCase();
      const esEgreso = tipo.includes("egreso") || tipo.includes("gasto");
      if (!esEgreso) return false;
      if (!coincideSucursal(e, sucursalSeleccionada)) return false;
      const dt = parseFechaDate(e.fecha || e.created_at);
      return dt && formatDateToYYYYMMDD(dt) === diaActivo;
    });
  }, [egresosIngresosList, sucursalSeleccionada, diaActivo, sucursales]);

  const ingresosDia = useMemo(() => {
    if (!diaActivo) return [];
    return egresosIngresosList.filter((i) => {
      const tipo = `${i.operacion ?? i.tipo ?? i.tipoMovimiento ?? ""}`.toLowerCase();
      const esIngreso = tipo.includes("ingreso") || tipo.includes("entrada");
      if (!esIngreso) return false;
      if (!coincideSucursal(i, sucursalSeleccionada)) return false;
      const dt = parseFechaDate(i.fecha || i.created_at);
      return dt && formatDateToYYYYMMDD(dt) === diaActivo;
    });
  }, [egresosIngresosList, sucursalSeleccionada, diaActivo, sucursales]);

  // Desglose de Pagos recibidos por Solicitud (Soporta múltiples pagos desglosados como en Corte de Caja)
  const getDesglosePagoSolicitud = (sol) => {
    const cajaObj = cajaMap[sol.idSolicitud] || {};
    const pagos = pagosPorSolicitudMap[sol.idSolicitud] || [];

    const totalSol = Number(cajaObj.totalPagar ?? cajaObj.total ?? sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const descuento = Number(cajaObj.descuento ?? sol.descuentoTotal ?? sol.descuento ?? sol.descuentoMonto ?? 0);
    const iva = Number(cajaObj.iva ?? sol.iva ?? sol.montoIva ?? 0);

    // Subtotal de la caja (precio bruto de los estudios sin descuento ni IVA)
    const subtotal = Number(
      cajaObj.subtotal ??
      sol.subtotal ??
      sol.subtotalCaja ??
      sol.subtotalSolicitud ??
      (totalSol + descuento - iva)
    );

    let ef = 0,
      tj = 0,
      ch = 0,
      tr = 0;

    const detallesCobroList = [];

    if (pagos.length > 0) {
      pagos.forEach((p) => {
        const montoP = Number(p.monto || p.cantidad || p.importe || 0);
        const metodo = `${p.formaPago || p.metodoPago || p.tipoPago || ""}`.toLowerCase();
        const empNom = [p.nombreUsuario, p.apPaternoUsuario, p.apMaternoUsuario].filter(Boolean).join(" ").trim() || p.usuario || p.cobro || "Cajero";
        const fechaStr = p.fecha || p.fechaCobro || sol.fecha || getFechaHoy();
        const formaFmt = p.formaPago || p.metodoPago || "Efectivo";

        if (metodo.includes("tarjeta")) {
          tj += montoP;
        } else if (metodo.includes("cheque")) {
          ch += montoP;
        } else if (metodo.includes("transferencia") || metodo.includes("transf")) {
          tr += montoP;
        } else {
          ef += montoP;
        }

        detallesCobroList.push({
          cobroEmpleado: empNom,
          formaPago: formaFmt,
          monto: montoP,
          fechaStr: fechaStr,
        });
      });
    } else {
      let pagadoUnico = 0;
      if (cajaObj.pagado !== undefined && cajaObj.pagado !== null && cajaObj.pagado !== "") {
        pagadoUnico = Number(cajaObj.pagado);
      } else if (sol.pagado !== undefined && sol.pagado !== null && sol.pagado !== "") {
        pagadoUnico = Number(sol.pagado);
      } else if (sol.aCuenta !== undefined && sol.aCuenta !== null && sol.aCuenta !== "") {
        pagadoUnico = Number(sol.aCuenta);
      } else {
        const estatus = `${sol.estatus ?? cajaObj.estatus ?? ""}`.trim().toUpperCase();
        if (estatus === "P") pagadoUnico = totalSol;
      }

      if (pagadoUnico > 0) {
        const forma = `${cajaObj.formaPago || sol.formaPago || sol.metodoPago || ""}`.toLowerCase();
        const formaFmt = cajaObj.formaPago || sol.formaPago || "Efectivo";
        const empNom = cajaObj.usuarioCobro || sol.usuarioCobro || sol.empleado || sol.usuario || (currentU ? `${currentU.nombre || ""} ${currentU.apPaterno || ""}`.trim() : "") || "Cajero";
        const fechaStr = cajaObj.fechaCobro || sol.fechaCobro || sol.fecha || getFechaHoy();

        if (forma.includes("tarjeta")) tj = pagadoUnico;
        else if (forma.includes("cheque")) ch = pagadoUnico;
        else if (forma.includes("transferencia") || forma.includes("transf")) tr = pagadoUnico;
        else ef = pagadoUnico;

        detallesCobroList.push({
          cobroEmpleado: empNom,
          formaPago: formaFmt,
          monto: pagadoUnico,
          fechaStr: fechaStr,
        });
      }
    }

    const totalPagadoReal = ef + tj + ch + tr;
    const tienePago = totalPagadoReal > 0;

    let adeudo = 0;
    if (cajaObj.adeudo !== undefined && cajaObj.adeudo !== null && cajaObj.adeudo !== "") {
      adeudo = Number(cajaObj.adeudo);
    } else if (sol.saldoPendiente !== undefined && sol.saldoPendiente !== null && sol.saldoPendiente !== "") {
      adeudo = Number(sol.saldoPendiente);
    } else if (sol.adeudo !== undefined && sol.adeudo !== null && sol.adeudo !== "") {
      adeudo = Number(sol.adeudo);
    } else {
      adeudo = Math.max(0, totalSol - totalPagadoReal);
    }

    const empleadoCobroStr = tienePago
      ? detallesCobroList.map((d) => d.cobroEmpleado).join(", ")
      : "-";

    return {
      subtotal,
      descuento,
      iva,
      adeudo,
      total: totalSol,
      pagado: totalPagadoReal,
      tienePago,
      efectivo: ef,
      tarjeta: tj,
      cheque: ch,
      transferencia: tr,
      empleadoCobro: empleadoCobroStr,
      detallesCobroList,
    };
  };

  // Cálculo del RESUMEN DE CORTE DE CAJA para el día activo
  const resumenDia = useMemo(() => {
    let totalSolicitudesEfectivo = 0;
    let totalTarjeta = 0;
    let totalCheque = 0;
    let totalTransferencia = 0;

    solicitudesDia.forEach((sol) => {
      const d = getDesglosePagoSolicitud(sol);
      totalSolicitudesEfectivo += d.efectivo;
      totalTarjeta += d.tarjeta;
      totalCheque += d.cheque;
      totalTransferencia += d.transferencia;
    });

    const totalReembolsos = reembolsosDia.reduce(
      (acc, r) => acc + Number(r.monto || r.cantidad || r.montoReembolso || 0),
      0
    );

    const totalGastos = egresosDia.reduce(
      (acc, g) => acc + Number(g.monto || g.cantidad || 0),
      0
    );

    const totalOtrosIngresos = ingresosDia.reduce(
      (acc, i) => acc + Number(i.monto || i.cantidad || 0),
      0
    );

    const efectivoTS_R = totalSolicitudesEfectivo - totalReembolsos;
    const efectivoTS_R_G_I = totalSolicitudesEfectivo - totalReembolsos - totalGastos + totalOtrosIngresos;

    const totalGeneral =
      totalSolicitudesEfectivo -
      totalReembolsos -
      totalGastos +
      totalOtrosIngresos +
      totalTarjeta +
      totalCheque +
      totalTransferencia;

    return {
      totalSolicitudesEfectivo,
      totalReembolsos,
      totalGastos,
      totalOtrosIngresos,
      efectivoTS_R,
      efectivoTS_R_G_I,
      totalTarjeta,
      totalCheque,
      totalTransferencia,
      totalGeneral,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudesDia, reembolsosDia, egresosDia, ingresosDia]);

  // Lista de Descuentos Posteriores para el Día Activo (Solo los registrados en el historial de la solicitud)
  const descuentosPosterioresDia = useMemo(() => {
    const list = [];
    solicitudesDia.forEach((sol) => {
      const evts = historialEventosMap[sol.idSolicitud] || [];
      const descEvts = evts.filter((e) =>
        `${e.operacion || e.tipo || e.evento || ""}`.toLowerCase().includes("descuent")
      );

      descEvts.forEach((descEvt) => {
        const descMonto = Number(sol.descuentoTotal ?? sol.descuento ?? sol.descuentoMonto ?? 0);
        const empEvt = descEvt?.nombreEmpleado || descEvt?.usuario || sol.nombreInserto || sol.usuarioCobro || "Sistema";
        const fechaDesc = descEvt?.fecha || sol.fecha || getFechaHoy();
        const conceptoText = descEvt?.operacion || sol.motivoDescuento || "Descuento posterior en solicitud";

        list.push({
          codigo: sol.codigo || sol.idSolicitud,
          paciente: `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim() || "Paciente no especificado",
          concepto: conceptoText,
          cantidad: descMonto > 0 ? descMonto : 0,
          fechaSolicitud: sol.fecha || sol.fechaCreacion || getFechaHoy(),
          fechaDescuento: fechaDesc,
          empleado: empEvt,
        });
      });
    });
    return list;
  }, [solicitudesDia, historialEventosMap]);

  // Generación de PDF Institucional
  const handleExportarPDF = async () => {
    if (solicitudesRangoCompleto.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No hay solicitudes registradas en el rango de fechas seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF();
        const startY = 12;

    let sucursalActualObj = sucursales.find(
      (s) => String(s.idSucursal) === String(sucursalSeleccionada) || s.nombre === sucursalSeleccionada
    ) || sucursales[0] || {};

    let sucursalTelefonosStr = "";
    if (sucursalActualObj.idSucursal) {
      try {
        const resTels = await TelefonosService.getTelefonosPorSucursal(sucursalActualObj.idSucursal);
        const listaTels = Array.isArray(resTels) ? resTels : resTels?.telefonos || [];
        sucursalTelefonosStr = listaTels.map((t) => t.telefono || t).join(", ");
      } catch (e) { }
    }

    const sucursalNomText = (sucursalActualObj.nombre || "SUCURSAL").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;
    const telText = `Tel. ${sucursalTelefonosStr || sucursalActualObj.telefono || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 28);
    } catch (e) { }

    // Rectángulo institucional "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Datos de la sucursal
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.text(colCpText, 196, currY, { align: "right" });
    currY += 4;
    doc.text(telText, 196, currY, { align: "right" });

    // Título de Sección
    currY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 35, 79);
    doc.text(`REPORTE DE CAJA - SUCURSAL: ${sucursalNomText}`, 14, currY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA}`, 14, currY + 4);

    // 1. Tabla Completa de Solicitudes del Rango
    const filasPdfSol = solicitudesRangoCompleto.map((sol) => {
      const d = getDesglosePagoSolicitud(sol);
      const paciente = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
      return [
        sol.codigo || sol.idSolicitud,
        paciente,
        `$${d.efectivo.toFixed(2)}`,
        `$${d.tarjeta.toFixed(2)}`,
        `$${d.cheque.toFixed(2)}`,
        `$${d.transferencia.toFixed(2)}`,
        `$${d.descuento.toFixed(2)}`,
        `$${d.iva.toFixed(2)}`,
        `$${d.adeudo.toFixed(2)}`,
        `$${d.subtotal.toFixed(2)}`,
        d.detallesCobroList.length > 0
          ? d.detallesCobroList.map((det) => `${det.cobroEmpleado} (${det.formaPago}: $${det.monto.toFixed(2)})`).join("\n")
          : "-",
      ];
    });

    autoTable(doc, {
      startY: currY + 8,
      head: [
        [
          "# Solicitud",
          "Paciente",
          "Efectivo",
          "Tarjeta",
          "Cheque",
          "Transf.",
          "Descuento",
          "IVA",
          "Adeudo",
          "Total",
          "Cobró",
        ],
      ],
      body: filasPdfSol,
      headStyles: { fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { fontSize: 7, cellPadding: 2 },
    });

    let nextY = doc.lastAutoTable.finalY + 8;

    // 2. Tabla Completa de Reembolsos del Rango
    const filasPdfReembolsos = reembolsosRangoCompleto.map((r) => {
      const nomUsuario = [r.nombreUsuario, r.apPaternoUsuario, r.apMaternoUsuario].filter(Boolean).join(" ").trim();
      return [
        r.idReembolso ?? r.codigo ?? "-",
        r.fecha ?? "-",
        r.motivo ?? r.concepto ?? "-",
        `$${Number(r.monto ?? r.cantidad ?? 0).toFixed(2)}`,
        r.formaPago ?? r.formaReembolso ?? "-",
        nomUsuario || r.usuario || "Cajero",
      ];
    });

    autoTable(doc, {
      startY: nextY,
      head: [["# / Código Reembolso", "Fecha", "Motivo", "Monto Reembolsado", "Forma Reembolso", "Usuario / Empleado"]],
      body: filasPdfReembolsos.length > 0 ? filasPdfReembolsos : [["-", "-", "No hay reembolsos en el rango de fechas", "$0.00", "-", "-"]],
      headStyles: { fillColor: [211, 47, 47], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { fontSize: 7, cellPadding: 2 },
    });

    nextY = doc.lastAutoTable.finalY + 8;

    // 3. Tabla Completa de Gastos (Egresos) del Rango
    const filasPdfEgresos = egresosRangoCompleto.map((g, idx) => {
      const emp = [g.nombreUsuario, g.apPaternoUsuario, g.apMaternoUsuario].filter(Boolean).join(" ").trim() || g.usuario || g.empleado || "Cajero";
      return [
        g.idEgresoIngreso ?? idx + 1,
        emp,
        g.concepto || "Gasto general",
        `$${Number(g.monto || g.cantidad || 0).toFixed(2)}`,
        g.formaPago || "Efectivo",
      ];
    });

    autoTable(doc, {
      startY: nextY,
      head: [["Código Gasto", "Empleado", "Concepto (Egreso)", "Cantidad", "Forma Pago"]],
      body: filasPdfEgresos.length > 0 ? filasPdfEgresos : [["-", "-", "No hay gastos en el rango de fechas", "$0.00", "-"]],
      headStyles: { fillColor: [198, 40, 40], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { fontSize: 7, cellPadding: 2 },
    });

    nextY = doc.lastAutoTable.finalY + 8;

    // 4. Tabla Completa de Otros Ingresos del Rango
    const filasPdfIngresos = ingresosRangoCompleto.map((ing, idx) => {
      const emp = [ing.nombreUsuario, ing.apPaternoUsuario, ing.apMaternoUsuario].filter(Boolean).join(" ").trim() || ing.usuario || ing.empleado || "Cajero";
      return [
        ing.idEgresoIngreso ?? idx + 1,
        emp,
        ing.concepto || "Ingreso extraordinario",
        `$${Number(ing.monto || ing.cantidad || 0).toFixed(2)}`,
        ing.formaPago || "Efectivo",
      ];
    });

    autoTable(doc, {
      startY: nextY,
      head: [["Código Ingreso", "Empleado", "Concepto (Otro Ingreso)", "Cantidad", "Forma Pago"]],
      body: filasPdfIngresos.length > 0 ? filasPdfIngresos : [["-", "-", "No hay otros ingresos en el rango de fechas", "$0.00", "-"]],
      headStyles: { fillColor: [46, 125, 50], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { fontSize: 7, cellPadding: 2 },
    });

    nextY = doc.lastAutoTable.finalY + 10;

    // 5. Resumen de Totales del Rango Completo
    const totalEfectivoRango = solicitudesRangoCompleto.reduce((acc, s) => acc + getDesglosePagoSolicitud(s).efectivo, 0);
    const totalTarjetaRango = solicitudesRangoCompleto.reduce((acc, s) => acc + getDesglosePagoSolicitud(s).tarjeta, 0);
    const totalChequeRango = solicitudesRangoCompleto.reduce((acc, s) => acc + getDesglosePagoSolicitud(s).cheque, 0);
    const totalTransfRango = solicitudesRangoCompleto.reduce((acc, s) => acc + getDesglosePagoSolicitud(s).transferencia, 0);
    const totalDescuentosRango = solicitudesRangoCompleto.reduce((acc, s) => acc + getDesglosePagoSolicitud(s).descuento, 0);
    const totalAdeudosRango = solicitudesRangoCompleto.reduce((acc, s) => acc + getDesglosePagoSolicitud(s).adeudo, 0);
    const totalGeneralRango = solicitudesRangoCompleto.reduce((acc, s) => acc + getDesglosePagoSolicitud(s).total, 0);

    const totalReembolsosRango = reembolsosRangoCompleto.reduce((acc, r) => acc + Number(r.monto || r.cantidad || 0), 0);
    const totalGastosRango = egresosRangoCompleto.reduce((acc, g) => acc + Number(g.monto || g.cantidad || 0), 0);
    const totalIngresosRango = ingresosRangoCompleto.reduce((acc, i) => acc + Number(i.monto || i.cantidad || 0), 0);

    autoTable(doc, {
      startY: nextY,
      head: [["RESUMEN DE TOTALES DEL RANGO", "MONTO ACUMULADO"]],
      body: [
        ["Total Efectivo Solicitudes", `$${totalEfectivoRango.toFixed(2)}`],
        ["Total Reembolsos", `$${totalReembolsosRango.toFixed(2)}`],
        ["Total Gastos (Egresos)", `$${totalGastosRango.toFixed(2)}`],
        ["Total Otros Ingresos", `$${totalIngresosRango.toFixed(2)}`],
        ["Total Tarjeta", `$${totalTarjetaRango.toFixed(2)}`],
        ["Total Cheque", `$${totalChequeRango.toFixed(2)}`],
        ["Total Transferencia", `$${totalTransfRango.toFixed(2)}`],
        ["Total Descuentos", `$${totalDescuentosRango.toFixed(2)}`],
        ["Total Adeudos Pendientes", `$${totalAdeudosRango.toFixed(2)}`],
        ["GRAN TOTAL ACUMULADO", `$${totalGeneralRango.toFixed(2)}`],
      ],
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { fontSize: 8 },
    });

    // Pie de Página en cada hoja
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Reporte_Caja_${sucursalNomText}_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Caja en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE DE CAJA
                </Typography>
              </Box>

              {/* Barra de Filtros Superiores */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "8px 12px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.1rem" }}>
                        Filtros de Control
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                        >
                          <MenuItem value="Todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal} value={String(suc.idSucursal)}>
                              {suc.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Doctor:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={doctorSeleccionado}
                          onChange={(e) => setDoctorSeleccionado(e.target.value)}
                        >
                          <MenuItem value="Todos">Todos los doctores</MenuItem>
                          {doctores.map((doc) => {
                            const esVigente = doc.estatus === "A" || doc.vigente === 1 || doc.activo === true;
                            const tag = esVigente ? "(Vigente)" : "(Baja)";
                            const nom = `${doc.nombre || ""} ${doc.apPaterno || ""} ${doc.apMaterno || ""}`.trim();
                            return (
                              <MenuItem key={doc.idDoctor} value={String(doc.idDoctor)}>
                                {`${nom} ${tag}`}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Rango de fechas:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            De:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            A:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />

                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setSucursalSeleccionada("Todas");
                              setDoctorSeleccionado("Todos");
                              setFechaDe(getFechaHoy());
                              setFechaA(getFechaHoy());
                            }}
                            startIcon={<RefreshIcon />}
                            sx={{ backgroundColor: "#18234f", color: "white", ml: 2 }}
                          >
                            Restablecer
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Contenido Dinámico según Filtros */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <>
                  {/* 1. Sección: Resumen de Corte de Caja */}
                  <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#18234f", mb: 1.5, textAlign: "center" }}>
                      RESUMEN DE CORTE DE CAJA ({diaActivo})
                    </Typography>

                    <Table
                      sx={{
                        width: "100%",
                        backgroundColor: "white",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        overflow: "hidden",
                        "& .MuiTableCell-root": { padding: "10px 12px" },
                      }}
                    >
                      <TableHead sx={{ backgroundColor: "#18234f" }}>
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ffffff40" }}>
                            Detalle pagos efectivo
                          </TableCell>
                          <TableCell colSpan={5} align="center" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ffffff40" }}>
                            Total en caja
                          </TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                            Total general
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Total solicitudes</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Reembolsos</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Gastos</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Otros ingresos</TableCell>

                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Efectivo (TS-R)</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Efectivo (TS-R-G+I)</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Tarjeta</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Cheque</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>Transferencia</TableCell>

                          <TableCell align="center" sx={{ color: "white", fontSize: "0.85rem" }}>(TS-R-G+I+T+C+TR)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>${resumenDia.totalSolicitudesEfectivo.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ color: "#d32f2f", fontWeight: "bold" }}>${resumenDia.totalReembolsos.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ color: "#d32f2f", fontWeight: "bold" }}>${resumenDia.totalGastos.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ color: "#2e7d32", fontWeight: "bold" }}>${resumenDia.totalOtrosIngresos.toFixed(2)}</TableCell>

                          <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#eef2f6" }}>${resumenDia.efectivoTS_R.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#eef2f6" }}>${resumenDia.efectivoTS_R_G_I.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>${resumenDia.totalTarjeta.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>${resumenDia.totalCheque.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>${resumenDia.totalTransferencia.toFixed(2)}</TableCell>

                          <TableCell align="center" sx={{ fontSize: "1.1rem", fontWeight: "bold", color: "#1b5e20", backgroundColor: "#e8f5e9" }}>
                            ${resumenDia.totalGeneral.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>

                  {/* 2. Detalle Informativo de Descuentos Posteriores */}
                  <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#18234f", mb: 1 }}>
                      Detalle informativo de descuentos posteriores ({diaActivo}):
                    </Typography>
                    <Table sx={{ width: "100%", backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                      <TableHead sx={{ backgroundColor: "#546e7a" }}>
                        <TableRow>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Código</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paciente</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Concepto</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Cantidad</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Fecha Solicitud</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Fecha Descuento</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Empleado que realizó descuento</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {descuentosPosterioresDia.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ color: "#888", py: 3 }}>
                              No hay descuentos registrados para la fecha seleccionada ({diaActivo}).
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {descuentosPosterioresDia.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.codigo}</TableCell>
                                <TableCell>{item.paciente}</TableCell>
                                <TableCell>{item.concepto}</TableCell>
                                <TableCell sx={{ fontWeight: "bold", color: "#d32f2f" }}>${item.cantidad.toFixed(2)}</TableCell>
                                <TableCell>{item.fechaSolicitud}</TableCell>
                                <TableCell>{item.fechaDescuento}</TableCell>
                                <TableCell>{item.empleado}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ backgroundColor: "#eceff1" }}>
                              <TableCell colSpan={3} sx={{ fontWeight: "bold", textAlign: "right" }}>Total Descuentos:</TableCell>
                              <TableCell sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                ${descuentosPosterioresDia.reduce((a, b) => a + b.cantidad, 0).toFixed(2)}
                              </TableCell>
                              <TableCell colSpan={3} />
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </Box>

                  {/* 3. CONTROL TRADICIONAL COMPACTO (Máximo 10 días visibles a la vez, se desplaza conforme se avanza) */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      my: 2,
                      py: 0.8,
                      px: 2,
                      backgroundColor: "#ffffff",
                      borderRadius: "20px",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                      border: "1px solid #c4d1db",
                      width: "fit-content",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#18234f", mr: 0.5 }}>
                      Día:
                    </Typography>

                    <IconButton
                      size="small"
                      disabled={paginaDia <= 1}
                      onClick={() => setPaginaDia((prev) => Math.max(1, prev - 1))}
                      sx={{ color: "#18234f", "&:disabled": { color: "#cccccc" } }}
                    >
                      <NavigateBeforeIcon fontSize="small" />
                    </IconButton>

                    {paginasVisibles.map((numPag) => {
                      const esActivo = numPag === paginaDia;
                      return (
                        <IconButton
                          key={numPag}
                          size="small"
                          onClick={() => setPaginaDia(numPag)}
                          sx={{
                            width: "28px",
                            height: "28px",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            backgroundColor: esActivo ? "#18234f" : "transparent",
                            color: esActivo ? "#ffffff" : "#18234f",
                            border: esActivo ? "none" : "1px solid #18234f",
                            "&:hover": {
                              backgroundColor: esActivo ? "#014e65" : "#e0e0e0",
                            },
                          }}
                        >
                          {numPag}
                        </IconButton>
                      );
                    })}

                    <IconButton
                      size="small"
                      disabled={paginaDia >= (diasConRegistros.length || 1)}
                      onClick={() => setPaginaDia((prev) => Math.min(diasConRegistros.length || 1, prev + 1))}
                      sx={{ color: "#18234f", "&:disabled": { color: "#cccccc" } }}
                    >
                      <NavigateNextIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="caption" sx={{ color: "#006787", fontWeight: "bold", ml: 1 }}>
                      ({diaActivo})
                    </Typography>
                  </Box>

                  {/* 4. Tabla Solicitudes por Sucursal (Detalle del día) */}
                  <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#18234f", mb: 2 }}>
                      Solicitudes del Día ({diaActivo})
                    </Typography>

                    {solicitudesDia.length === 0 ? (
                      <Typography variant="body2" sx={{ fontStyle: "italic", color: "#777", textAlign: "center", py: 3 }}>
                        No hay solicitudes registradas para el día seleccionado ({diaActivo}).
                      </Typography>
                    ) : (
                      <Table sx={{ width: "100%", backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                        <TableHead sx={{ backgroundColor: "#18234f" }}>
                          <TableRow>
                            <TableCell rowSpan={2} sx={{ color: "white", fontWeight: "bold" }}># Solicitud</TableCell>
                            <TableCell rowSpan={2} sx={{ color: "white", fontWeight: "bold" }}>Paciente</TableCell>
                            <TableCell colSpan={4} align="center" sx={{ color: "white", fontWeight: "bold", borderBottom: "1px solid #ffffff40" }}>
                              Pagos recibidos en caja
                            </TableCell>
                            <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>Descuento</TableCell>
                            <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>IVA</TableCell>
                            <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>Adeudos</TableCell>
                            <TableCell rowSpan={2} align="center" sx={{ color: "white", fontWeight: "bold" }}>Total solicitud</TableCell>
                            <TableCell rowSpan={2} sx={{ color: "white", fontWeight: "bold" }}>Realizó cobro</TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: "#006787" }}>
                            <TableCell align="center" sx={{ color: "white", fontSize: "0.8rem" }}>Efectivo</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontSize: "0.8rem" }}>Tarjeta</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontSize: "0.8rem" }}>Cheque</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontSize: "0.8rem" }}>Transf.</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {solicitudesDia.map((sol) => {
                            const d = getDesglosePagoSolicitud(sol);
                            const paciente = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
                            const tieneMotivo = sol.motivoDescuento && sol.motivoDescuento.trim() !== "";

                            return (
                              <React.Fragment key={sol.idSolicitud || sol.codigo}>
                                <TableRow sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                                  <TableCell sx={{ fontWeight: "bold" }}>{sol.codigo || sol.idSolicitud}</TableCell>
                                  <TableCell>{paciente}</TableCell>
                                  <TableCell align="center">${d.efectivo.toFixed(2)}</TableCell>
                                  <TableCell align="center">${d.tarjeta.toFixed(2)}</TableCell>
                                  <TableCell align="center">${d.cheque.toFixed(2)}</TableCell>
                                  <TableCell align="center">${d.transferencia.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ color: "#d32f2f" }}>${d.descuento.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ color: "#1976d2" }}>${d.iva.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ color: d.adeudo > 0 ? "#d32f2f" : "#2e7d32" }}>
                                    ${d.adeudo.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold" }}>${d.subtotal.toFixed(2)}</TableCell>
                                  <TableCell>
                                    {d.detallesCobroList.length > 0 ? (
                                      d.detallesCobroList.map((det, detIdx) => (
                                        <Box
                                          key={detIdx}
                                          sx={{
                                            py: 0.5,
                                            borderBottom: detIdx < d.detallesCobroList.length - 1 ? "1px dashed #e0e0e0" : "none",
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                            {det.cobroEmpleado}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: "#555" }}>
                                            [{det.formaTexto}: ${det.monto.toFixed(2)} - {det.fechaStr}]
                                          </Typography>
                                        </Box>
                                      ))
                                    ) : (
                                      <Typography variant="body2" sx={{ color: "#888", fontStyle: "italic" }}>
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>

                                {tieneMotivo && (
                                  <TableRow sx={{ backgroundColor: "#fff8e1" }}>
                                    <TableCell colSpan={11} sx={{ fontStyle: "italic", fontSize: "0.85rem", color: "#b78103", pl: 4 }}>
                                      Motivo descuento: {sol.motivoDescuento}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}

                          {/* Totales por columna */}
                          <TableRow sx={{ backgroundColor: "#eef2f6", borderTop: "2px solid #18234f" }}>
                            <TableCell colSpan={2} sx={{ fontWeight: "bold", textAlign: "right" }}>Totales del Día:</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).efectivo, 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).tarjeta, 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).cheque, 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).transferencia, 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).descuento, 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).iva, 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).adeudo, 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>
                              ${solicitudesDia.reduce((a, s) => a + getDesglosePagoSolicitud(s).subtotal, 0).toFixed(2)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    )}
                  </Box>

                  {/* Detalle Reembolsos */}
                  <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#18234f", mb: 1 }}>
                      Detalle Reembolsos ({diaActivo}):
                    </Typography>
                    <Table sx={{ width: "100%", backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                      <TableHead sx={{ backgroundColor: "#d32f2f" }}>
                        <TableRow>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Código / ID</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Fecha</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Motivo</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Cantidad Reembolsado</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Forma de Reembolso</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Reembolso (Empleado)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reembolsosDia.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ color: "#888", py: 3 }}>
                              No hay reembolsos registrados para el día seleccionado ({diaActivo}).
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {reembolsosDia.map((r, idx) => {
                              const nomUsuario = [r.nombreUsuario, r.apPaternoUsuario, r.apMaternoUsuario].filter(Boolean).join(" ").trim();
                              return (
                                <TableRow key={r.idReembolso ? `reem-${r.idReembolso}` : idx}>
                                  <TableCell sx={{ fontWeight: "bold" }}>{r.idReembolso ?? r.codigo ?? "-"}</TableCell>
                                  <TableCell>{r.fecha ?? "-"}</TableCell>
                                  <TableCell>{r.motivo ?? r.concepto ?? "-"}</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                    ${Number(r.monto ?? r.cantidad ?? 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell>{r.formaPago ?? r.formaReembolso ?? "-"}</TableCell>
                                  <TableCell>{nomUsuario || r.usuario || "Cajero"}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow sx={{ backgroundColor: "#e9f2f9" }}>
                              <TableCell colSpan={3} sx={{ fontWeight: "bold", textAlign: "right" }}>Total Reembolsos:</TableCell>
                              <TableCell sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                ${reembolsosDia.reduce((sum, r) => sum + Number(r.monto ?? r.cantidad ?? 0), 0).toFixed(2)}
                              </TableCell>
                              <TableCell colSpan={2} />
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </Box>

                  {/* Detalle Gastos e Ingresos */}
                  <Grid container spacing={3} sx={{ width: "95%", maxWidth: "1200px", mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#d32f2f", mb: 1 }}>
                        Detalle de Gastos (Egresos del Día):
                      </Typography>
                      <Table sx={{ width: "100%", backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                        <TableHead sx={{ backgroundColor: "#c62828" }}>
                          <TableRow>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Código</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Empleado</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Concepto</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Cantidad</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Forma Pago</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {egresosDia.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ color: "#888", py: 3 }}>No hay gastos registrados hoy ({diaActivo}).</TableCell>
                            </TableRow>
                          ) : (
                            egresosDia.map((g, idx) => {
                              const emp = [g.nombreUsuario, g.apPaternoUsuario, g.apMaternoUsuario].filter(Boolean).join(" ").trim() || g.usuario || g.empleado || "Cajero";
                              return (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontWeight: "bold" }}>{g.idEgresoIngreso ?? idx + 1}</TableCell>
                                  <TableCell>{emp}</TableCell>
                                  <TableCell>{g.concepto || "Gasto general"}</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                                    ${Number(g.monto || g.cantidad || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell>{g.formaPago || "Efectivo"}</TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#2e7d32", mb: 1 }}>
                        Detalle de Otros Ingresos (del Día):
                      </Typography>
                      <Table sx={{ width: "100%", backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                        <TableHead sx={{ backgroundColor: "#2e7d32" }}>
                          <TableRow>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Código</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Empleado</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Concepto</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Cantidad</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Forma Pago</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ingresosDia.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ color: "#888", py: 3 }}>No hay otros ingresos registrados hoy ({diaActivo}).</TableCell>
                            </TableRow>
                          ) : (
                            ingresosDia.map((ing, idx) => {
                              const emp = [ing.nombreUsuario, ing.apPaternoUsuario, ing.apMaternoUsuario].filter(Boolean).join(" ").trim() || ing.usuario || ing.empleado || "Cajero";
                              return (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontWeight: "bold" }}>{ing.idEgresoIngreso ?? idx + 1}</TableCell>
                                  <TableCell>{emp}</TableCell>
                                  <TableCell>{ing.concepto || "Ingreso extraordinario"}</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                                    ${Number(ing.monto || ing.cantidad || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell>{ing.formaPago || "Efectivo"}</TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </Grid>
                  </Grid>
                </>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón Lateral Flotante para Exportar PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "2vw",
            top: "30%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default ReporteCaja;
